import { useEffect } from 'react';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
  enabled?: boolean;
}

export const GoogleAnalytics = ({ measurementId, enabled = true }: GoogleAnalyticsProps) => {
  useEffect(() => {
    if (!enabled || !measurementId) return;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    // Initialize Google Analytics (pageviews tracked via RouteTracker)
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false, // Prevents duplicate initial pageview (RouteTracker handles all pageviews)
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(`script[src*="${measurementId}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [measurementId, enabled]);

  return null;
};

// Helper function to track events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Helper function to track page views
export const trackPageView = (measurementId: string, pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // ALWAYS read and send page_title (never omit it)
    // Priority: explicit pageTitle param → document.title → static fallback
    const title = pageTitle || document.title || 'Sink Bike Run';
    
    window.gtag('config', measurementId, {
      page_path: pagePath,
      page_title: title, // ALWAYS included
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }
};